// src/utils/duration.js

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Formats an elapsed duration for operational display: "4m", "1h 20m", "3d 4h".
 *
 * Deliberately coarse — the workspace shows how long an incident has been live,
 * not a stopwatch, so seconds only appear under a minute.
 */
export function formatDuration(ms) {
  if (ms == null || Number.isNaN(ms) || ms < 0) return "—";

  if (ms < MINUTE) {
    return `${Math.max(1, Math.floor(ms / 1000))}s`;
  }
  if (ms < HOUR) {
    return `${Math.floor(ms / MINUTE)}m`;
  }
  if (ms < DAY) {
    const hours = Math.floor(ms / HOUR);
    const minutes = Math.floor((ms % HOUR) / MINUTE);
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  const days = Math.floor(ms / DAY);
  const hours = Math.floor((ms % DAY) / HOUR);
  return hours ? `${days}d ${hours}h` : `${days}d`;
}

/** Milliseconds elapsed since a Firestore timestamp, or null if unavailable. */
export function millisSince(timestamp) {
  const seconds = timestamp?.seconds;
  if (!seconds) return null;
  return Date.now() - seconds * 1000;
}

/** Short absolute clock time for timeline entries: "14:05". */
export function formatClockTime(timestamp) {
  const seconds = timestamp?.seconds;
  if (!seconds) return "";
  return new Date(seconds * 1000).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Date heading for grouping timeline entries by day. */
export function formatDateHeading(timestamp) {
  const seconds = timestamp?.seconds;
  if (!seconds) return "";
  return new Date(seconds * 1000).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}
